import { ApiClient } from '@api/api-configuration';
import type {
  ChangedCaregiverView,
  PlanningSessionChangeView,
  PlanningSessionFlushView,
  PlanningSessionView,
  StaffingChangeView,
  TimingChangeView,
} from '@api/READ_ONLY/visits_api/Api';

/*
 * Thin wrappers over the generated `planningSessions` client.
 *
 * The types and the five calls are generated now — what is left here is the aliasing that keeps
 * this module the one seam the feature imports from, and the reasoning the generated docstrings
 * do not carry: what the dashboard must NOT do with these fields.
 */

/**
 * One planner's open snooze window.
 *
 * The bar counts down to `deliversAt`, never to `expiresAt`. Those differ whenever the night
 * hold applies — a session expiring at 02:00 delivers at 07:00 — and a bar counting down to
 * 02:00 and then sending nothing for five hours is a UI that lies about the system. The backend
 * owns the quiet-hours rule; the frontend must never reimplement it, because two
 * implementations of one rule drift and the one the planner can see is not the one that fires.
 *
 * `timezone` is the planner's STORED zone, and the publish warning is judged in it. The
 * browser's own zone is NOT a substitute: a planner on a laptop still set to another country
 * would be warned at the wrong hours, or not at all.
 */
export type PlanningSession = PlanningSessionView;

/** What a publish actually did. `published: false` means every send failed — see the schema. */
export type PlanningSessionFlush = PlanningSessionFlushView;

/** A caregiver on one side of a staffing change, named server-side so no second lookup is needed. */
export type PlanningSessionChangeCaregiver = ChangedCaregiverView;

/** The caregiver set as the session first saw it, and as it stands now. */
export type PlanningSessionStaffingChange = StaffingChangeView;

/** The window as the session first saw it, and as it stands now. */
export type PlanningSessionTimingChange = TimingChangeView;

/**
 * One visit's net change since the session first touched it.
 *
 * A row in the review panel is one visit × one field group, never one edit. `Maria → Eleni →
 * Nikos` arrives as the single row `Maria → Nikos`, so a revert can never clobber a later edit
 * it knows nothing about. A group that has not net-changed is `null` and produces no row; a
 * visit with both groups `null` is not in the response at all.
 */
export type PlanningSessionChange = PlanningSessionChangeView;

/** The open session, or `null` when there is none — the ordinary answer, not an error. */
export const getCurrentPlanningSession = async (): Promise<PlanningSession | null> => {
  const response = await ApiClient.visitsApi.planningSessions.getCurrentPlanningSession();
  return response.data ?? null;
};

/**
 * What has net-changed in the session, grouped by visit.
 *
 * Derived server-side by comparing the first-touch snapshot to the visit now, so there is no
 * event log to keep in step and a drag-and-drag-back simply stops being listed.
 */
export const getPlanningSessionChanges = async (planningSessionId: number): Promise<PlanningSessionChange[]> => {
  const response = await ApiClient.visitsApi.planningSessions.getPlanningSessionChanges(planningSessionId);
  return response.data ?? [];
};

/**
 * Opens a session, or returns the one already open.
 *
 * Idempotent by design — a partial unique index makes a second open session impossible, and a
 * planner opening one twice means one session, not an error.
 */
export const openPlanningSession = async (): Promise<PlanningSession> => {
  const response = await ApiClient.visitsApi.planningSessions.openPlanningSession();
  return response.data;
};

/** Closes the session and flushes: every affected caregiver gets one summary. */
export const completePlanningSession = async (planningSessionId: number): Promise<PlanningSessionFlush> => {
  const response = await ApiClient.visitsApi.planningSessions.completePlanningSession(planningSessionId);
  return response.data;
};

/**
 * Closes the session WITHOUT flushing.
 *
 * The fingerprints are untouched, so each visit notifies on its own merits from here on. This
 * is deliberately a different call from complete: "stop snoozing" and "publish the plan" are
 * different intentions and only one of them sends notifications.
 */
export const discardPlanningSession = async (planningSessionId: number): Promise<void> => {
  await ApiClient.visitsApi.planningSessions.discardPlanningSession(planningSessionId);
};
